import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import { useSearchParamsStore } from '@aibees/elephant/store/search-params-store';
import { useFullScreenLoadingContext } from '@aibees/shared/context';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPANY_ID_FILTER_SEPARATOR } from '../constants/filters';
import { ROUTES } from '../router';
import { SearchType } from '../types';
import { SearchListType } from './search-list/types';
import { useCreateSearchList } from './search-list/use-create-search-list';

export function useHandleFindButton({ type }: { type?: SearchType }) {
  const navigate = useNavigate();
  const { setParamForScope, removeAllParamsForScope } = useSearchParamsStore();

  const resetScopeHistory = useSearchHistoryStore.useReset();

  const resetHistoryForTargetScope = useCallback(() => {
    const targetScope: SearchTableScope =
      type === SearchType.Company ? 'people' : 'company';
    resetScopeHistory(targetScope);
  }, [type, resetScopeHistory]);
  const createSearchListApi = useCreateSearchList();
  const { reset: resetLoadingScreen } = useFullScreenLoadingContext();

  const redirectToSearchList = useCallback(
    (id: string, length?: number) => {
      const paramValue =
        length != null ? `${id}${COMPANY_ID_FILTER_SEPARATOR}${length}` : id;
      if (type === SearchType.Company) {
        removeAllParamsForScope('people');
        setParamForScope('people', 'people_company_search_list_id', paramValue);
        const search = new URLSearchParams({
          people_company_search_list_id: paramValue,
        });
        navigate(`${ROUTES.searchPeople}?${search.toString()}`);
      } else {
        removeAllParamsForScope('company');
        setParamForScope('company', 'company_search_list_id', paramValue);
        const search = new URLSearchParams({
          company_search_list_id: paramValue,
        });
        navigate(`${ROUTES.searchCompany}?${search.toString()}`);
      }
      setTimeout(() => {
        resetLoadingScreen();
      }, 200);
    },
    [
      type,
      removeAllParamsForScope,
      setParamForScope,
      navigate,
      resetLoadingScreen,
    ]
  );

  const createSearchList = useCallback(
    (ids?: string[]) => {
      if (!ids?.length) {
        resetLoadingScreen();
        return;
      }
      createSearchListApi
        .mutateAsync({
          type: SearchListType.COMPANY_ID,
          values: ids,
        })
        .then((payload) => {
          resetHistoryForTargetScope();
          return payload.id;
        })
        .then((id) => {
          redirectToSearchList(id, ids.length);
        })
        .catch((_error) => {
          resetLoadingScreen();
        });
    },
    [
      createSearchListApi,
      resetHistoryForTargetScope,
      redirectToSearchList,
      resetLoadingScreen,
    ]
  );

  return {
    createSearchListAndRedirect: createSearchList,
  };
}
